// Elección del ALMACENAMIENTO de un empleado al contratarlo, con su impacto en
// vivo. Lo usan los TRES caminos de contratación (portal cliente, portal partner
// y panel admin) por la misma razón por la que el backend tiene un solo
// normalize_storage/assert_storage_headroom: si cada portal ofreciera sus
// tamaños, alguno acabaría ofreciendo uno que el backend rechaza.
//
// Las reglas NO se inventan aquí — llegan en el resumen de almacenamiento de la
// cuenta (StorageSummaryOut) y son exactamente las que valida
// _assert_storage_shape en el backend:
//   · cada volumen ≥ su mínimo (min_workspace_gib / min_data_gib) y ≤ el tope
//     duro por volumen (max_volume_gib);
//   · el TOTAL del empleado va en tramos: base + k·step_gib, con
//     base = min_workspace_gib + min_data_gib (20 GiB = un empleado mínimo).
// Y el techo de la CUENTA (max_gib, 0 = sin techo) se comprueba aquí también,
// para decirlo ANTES de rellenar el formulario en vez de comerse un 409.
import React from 'react';
import { Alert, Box, FormHelperText, MenuItem, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { AccountStorage } from '../types/clientAccount';
import { formatCredits } from '../utils/credits';

export interface EmployeeVolumes {
  workspace_gib: number;
  data_gib: number;
}

// Constantes efectivas: las del backend cuando vienen, y si no las mismas que
// ese backend aplicaría de todos modos (10+10, tramo 20, tope 100). Nunca se
// deciden aquí valores distintos: sería inventar una regla nueva.
interface StorageRules {
  minWorkspace: number;
  minData: number;
  maxVolume: number;
  step: number;
  base: number;
  /** Granularidad del selector de UN volumen: medio tramo (10 GiB con step 20),
   *  que es la unidad más fina con la que dos volúmenes pueden cuadrar el
   *  tramo del total. */
  unit: number;
}

export const storageRules = (storage?: AccountStorage | null): StorageRules => {
  const minWorkspace = Number(storage?.min_workspace_gib ?? 10);
  const minData = Number(storage?.min_data_gib ?? 10);
  const step = Number(storage?.step_gib ?? 20);
  return {
    minWorkspace,
    minData,
    maxVolume: Number(storage?.max_volume_gib ?? 100),
    step,
    base: minWorkspace + minData,
    unit: Math.max(1, Math.floor(step / 2))
  };
};

// ¿El disco de esta cuenta lo factura el marketplace a su PARTNER? El backend
// lo marca así (y entonces manda precio 0 para la cuenta). Se lee sin exigir que
// el tipo lo declare: el contrato lo añade el mismo backend que hace el cargo, y
// esta UI no debe romper si va por delante o por detrás de él. Sin la bandera,
// «precio 0» se contaría como «no se cobra», que en una cuenta de cartera sería
// mentira — lo paga su partner.
const isBilledToPartner = (storage?: AccountStorage | null): boolean =>
  Boolean((storage as { billed_to_partner?: boolean } | null | undefined)?.billed_to_partner);

/** Volúmenes de salida = los mínimos (lo mismo que aplica normalize_storage
 *  cuando la petición no manda tamaños). */
export const defaultVolumes = (storage?: AccountStorage | null): EmployeeVolumes => {
  const r = storageRules(storage);
  return { workspace_gib: r.minWorkspace, data_gib: r.minData };
};

const isValidPair = (v: EmployeeVolumes, r: StorageRules): boolean =>
  v.workspace_gib >= r.minWorkspace
  && v.data_gib >= r.minData
  && v.workspace_gib <= r.maxVolume
  && v.data_gib <= r.maxVolume
  && (r.step <= 0 || (v.workspace_gib + v.data_gib - r.base) % r.step === 0);

const optionsFor = (min: number, r: StorageRules): number[] => {
  const out: number[] = [];
  for (let v = min; v <= r.maxVolume; v += r.unit) out.push(v);
  return out;
};

// Al mover UN volumen, el otro se ajusta al valor válido más cercano (y a
// igualdad de distancia, al MENOR: el ajuste automático nunca debe encarecer
// más de lo imprescindible). Sin esto el cliente podría quedarse atascado en
// una pareja que no cuadra el tramo, y bloquearle el botón por una regla que no
// ve sería peor que moverle 10 GiB explicándolo.
const snap = (changed: 'workspace' | 'data', next: EmployeeVolumes,
              r: StorageRules): EmployeeVolumes => {
  if (isValidPair(next, r)) return next;
  const partnerMin = changed === 'workspace' ? r.minData : r.minWorkspace;
  const current = changed === 'workspace' ? next.data_gib : next.workspace_gib;
  const candidates = optionsFor(partnerMin, r).filter((v) => {
    const pair: EmployeeVolumes = changed === 'workspace'
      ? { workspace_gib: next.workspace_gib, data_gib: v }
      : { workspace_gib: v, data_gib: next.data_gib };
    return isValidPair(pair, r);
  });
  if (candidates.length === 0) return next;
  candidates.sort((a, b) => Math.abs(a - current) - Math.abs(b - current) || a - b);
  const fixed = candidates[0];
  return changed === 'workspace'
    ? { workspace_gib: next.workspace_gib, data_gib: fixed }
    : { workspace_gib: fixed, data_gib: next.data_gib };
};

export interface StorageImpact {
  /** GiB que ocupará el empleado (la suma de sus dos volúmenes). */
  total: number;
  /** GiB que quedaban libres del incluido ANTES de este empleado. */
  includedFreeBefore: number;
  /** Cuánto del incluido consume este empleado (el resto ya va facturado). */
  includedConsumed: number;
  /** GiB libres del incluido que quedarían después. */
  includedFreeAfter: number;
  /** GiB facturables de la cuenta después de contratarlo. */
  billableAfter: number;
  /** Cargo por ciclo de la cuenta después (créditos). */
  cycleCreditsAfter: number;
  /** Lo que SUMA este empleado al cargo por ciclo (0 = cabe en lo incluido). */
  cycleCreditsDelta: number;
  /** Uso total de la cuenta tras contratarlo. */
  usedAfter: number;
  /** true = superaría el techo de la cuenta (el backend lo rechazaría). */
  overCap: boolean;
  /** Precio del GiB por ciclo; 0 = el almacenamiento no se cobra. */
  pricePerGib: number;
}

export const storageImpact = (storage: AccountStorage | null | undefined,
                              volumes: EmployeeVolumes): StorageImpact => {
  const total = Number(volumes.workspace_gib) + Number(volumes.data_gib);
  const used = Number(storage?.used_gib ?? 0);
  const included = Number(storage?.included_gib ?? 0);
  const cap = Number(storage?.max_gib ?? 0);
  const price = Number(storage?.credits_per_gib_cycle ?? 0) || 0;
  const usedAfter = used + total;
  const billableBefore = Math.max(0, used - included);
  const billableAfter = Math.max(0, usedAfter - included);
  const includedFreeBefore = Math.max(0, included - used);
  return {
    total,
    includedFreeBefore,
    includedConsumed: Math.min(total, includedFreeBefore),
    includedFreeAfter: Math.max(0, included - usedAfter),
    billableAfter,
    cycleCreditsAfter: price > 0 ? billableAfter * price : 0,
    cycleCreditsDelta: price > 0 ? (billableAfter - billableBefore) * price : 0,
    usedAfter,
    overCap: cap > 0 && usedAfter > cap,
    pricePerGib: price
  };
};

interface Props {
  /** Resumen de almacenamiento de la CUENTA (usado/incluido/techo/precio). Sin
   *  él (backend anterior a la 038) el bloque no se pinta: sin uso ni incluido
   *  no se puede decir la verdad del impacto, y adivinarlo sería peor. */
  storage?: AccountStorage | null;
  value: EmployeeVolumes;
  onChange: (next: EmployeeVolumes) => void;
  disabled?: boolean;
  /** ¿El techo de la cuenta BLOQUEA esta contratación? true (default) en los
   *  caminos self-service y partner, donde el backend lo hace cumplir
   *  (hire_employee → assert_storage_headroom). En el camino del ADMIN es false
   *  a propósito: assign_employee no aplica el techo —el admin asigna lo que
   *  decida— así que aquí se AVISA en vez de bloquear, que es justo lo que hace
   *  el backend. */
  capBlocks?: boolean;
}

const EmployeeStoragePicker: React.FC<Props> = ({ storage, value, onChange, disabled,
                                                 capBlocks = true }) => {
  const { t } = useTranslation();
  if (!storage) return null;

  const r = storageRules(storage);
  const impact = storageImpact(storage, value);
  const free = impact.cycleCreditsDelta <= 0;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('employeeStorage.title', 'Almacenamiento del empleado')}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          select
          margin="dense"
          size="small"
          sx={{ flex: '1 1 180px' }}
          label={t('employeeStorage.workspace', 'Espacio de trabajo del agente (GiB)')}
          value={String(value.workspace_gib)}
          disabled={disabled}
          onChange={(e) => onChange(snap('workspace', {
            workspace_gib: Number(e.target.value), data_gib: value.data_gib
          }, r))}
        >
          {optionsFor(r.minWorkspace, r).map((gib) => (
            <MenuItem key={gib} value={String(gib)}>{gib} GiB</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          margin="dense"
          size="small"
          sx={{ flex: '1 1 180px' }}
          label={t('employeeStorage.data', 'Datos de la aplicación (GiB)')}
          value={String(value.data_gib)}
          disabled={disabled}
          onChange={(e) => onChange(snap('data', {
            workspace_gib: value.workspace_gib, data_gib: Number(e.target.value)
          }, r))}
        >
          {optionsFor(r.minData, r).map((gib) => (
            <MenuItem key={gib} value={String(gib)}>{gib} GiB</MenuItem>
          ))}
        </TextField>
      </Box>
      <FormHelperText>
        {t('employeeStorage.rulesHelp',
          'El espacio de trabajo guarda lo que produce el agente; los datos, los de su aplicación. Mínimo {{min}} GiB por volumen, máximo {{max}}, y el total se compra en tramos de {{step}} GiB: al cambiar un volumen ajustamos el otro lo justo para cuadrar el tramo.',
          { min: r.minWorkspace, max: r.maxVolume, step: r.step })}
      </FormHelperText>
      {/* IMPACTO EN VIVO: lo que ocupa, lo que se come del incluido y lo que
          costará por ciclo. Enterarse de esto en la factura es exactamente lo
          que hay que evitar. */}
      <Alert
        severity={impact.overCap ? (capBlocks ? 'error' : 'warning') : (free ? 'info' : 'warning')}
        sx={{ mt: 1.5 }}
      >
        <Typography variant="body2">
          {t('employeeStorage.total', 'Ocupará {{total}} GiB ({{ws}} de espacio de trabajo + {{data}} de datos).', {
            total: impact.total, ws: value.workspace_gib, data: value.data_gib
          })}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {t('employeeStorage.included',
            'Consume {{consumed}} GiB de los {{included}} incluidos sin coste de la cuenta (quedarían {{left}} libres). Uso de la cuenta: {{usedAfter}} GiB.',
            {
              consumed: impact.includedConsumed,
              included: Number(storage.included_gib ?? 0),
              left: impact.includedFreeAfter,
              usedAfter: impact.usedAfter
            })}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {isBilledToPartner(storage)
            ? t('employeeStorage.billedToPartner',
                'El almacenamiento de esta cuenta lo lleva el partner que la gestiona: no genera cargo en el saldo de la cuenta.')
            : impact.pricePerGib <= 0
            ? t('employeeStorage.noPrice',
                'El almacenamiento no tiene precio en esta plataforma: sin coste.')
            : free
              ? t('employeeStorage.free',
                  'Cabe en lo incluido: sin coste de almacenamiento. Si más adelante la cuenta se pasa de lo incluido, el exceso sí se cobra por ciclo.')
              : t('employeeStorage.cost',
                  'Facturable: {{billable}} GiB · sumará {{delta}} créditos por ciclo mientras el empleado exista (total de la cuenta: {{after}} créditos/ciclo).',
                  {
                    billable: impact.billableAfter,
                    delta: formatCredits(impact.cycleCreditsDelta),
                    after: formatCredits(impact.cycleCreditsAfter)
                  })}
        </Typography>
        {impact.overCap && (
          <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
            {capBlocks
              ? t('employeeStorage.overCap',
                  'Con este tamaño la cuenta llegaría a {{after}} GiB y su límite es {{max}} GiB: elige un tamaño menor, retira un empleado o amplía el límite de la cuenta.',
                  { after: impact.usedAfter, max: Number(storage.max_gib ?? 0) })
              : t('employeeStorage.overCapAdmin',
                  'Con este tamaño la cuenta llegaría a {{after}} GiB y su límite es {{max}} GiB. La asignación del admin no lo bloquea, pero después el cliente no podrá contratar ni ampliar hasta que se suba el límite.',
                  { after: impact.usedAfter, max: Number(storage.max_gib ?? 0) })}
          </Typography>
        )}
      </Alert>
    </Box>
  );
};

export default EmployeeStoragePicker;
